// External Imports
import { createSelector } from 'reselect';

// Local Functions
/**
 * Get menu items from a menu in a particular position
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 */
const getMenu = createSelector([(state, position) => state.menu[position] || []], (menu) =>
  menu.map((menuItem) => ({
    ...menuItem,
    url: menuItem.fullUrl,
  })),
);

/**
 * Get the submenu items under a top-level item with a specified slug
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 * @param {number} id - id of top-level menu item for which you want submenu items
 */
const getSubNav = (state, position, id) => {
  const menu = getMenu(state, position);

  return menu.filter(({ parentId }) => id === parentId);
};

/**
 * Get menu items from a menu in a particular position,
 * with all submenu items nested in properties under their parent
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 */
const getMenuHierarchical = (state, position) => {
  const menu = getMenu(state, position);

  return menu
    .map((item) => ({
      subNav: getSubNav(state, position, item.id),
      ...item,
    }))
    .filter((item) => filterTopLevel(item));
};

/**
 * Get menu items from a menu in a particular position,
 * with all submenu items filtered out
 *
 * @param {object} state - current app state
 * @param {string} position - menu position name
 */
const getMenuTopLevel = (state, position) => {
  const menu = getMenu(state, position);

  return menu.filter((menuItem) => filterTopLevel(menuItem));
};

function filterTopLevel({ parentId }) {
  return !parentId;
}

// Module Exports
export { getMenu, getMenuHierarchical, getMenuTopLevel, getSubNav };
